/**
 * MAP mode (right column): the manual-correction list for an imported rig.
 *
 * Everything here edits `mapping.draft`; nothing binds until "Confirm mapping" (which lives in
 * the right footer, `mappingFooterPanel.ts`, so it is never scrolled away). Any draft edit clears
 * the stored validation (store rule), so Confirm goes back to disabled until Validate runs again
 * — exactly the intended "you changed it, prove it again" flow.
 */

import { CANONICAL_BONE_IDS, quatEquals } from '../../shared';
import type { CanonicalBoneId, QuaternionTuple } from '../../shared';
import type { EditorContext } from '../context';
import type { Controllers } from '../controllers/types';
import type { MappingConfidence } from '../types';
import { dialogs } from '../dialogs';
import { button, el, icon, select, type Option } from './dom';
import { createPanel, field, note, runAsync, section, type PanelHandle } from './panel';

const SQRT_HALF = Math.SQRT1_2;

/**
 * World correction = the rotation taking the source model's world frame to the canonical one
 * (Y up, facing +Z). Only whole quarter turns about Y are offered; anything else stays "custom".
 */
const FACINGS: ReadonlyArray<{ value: string; label: string; q: QuaternionTuple }> = [
  { value: '+Z', label: 'model faces +Z (canonical)', q: [0, 0, 0, 1] },
  { value: '-Z', label: 'model faces −Z', q: [0, 1, 0, 0] },
  { value: '+X', label: 'model faces +X', q: [0, -SQRT_HALF, 0, SQRT_HALF] },
  { value: '-X', label: 'model faces −X', q: [0, SQRT_HALF, 0, SQRT_HALF] },
];

/** The confidence is a colour stripe on the select; this is the same fact in words, on hover. */
const CONFIDENCE_TITLE: Record<MappingConfidence, string> = {
  confident: 'confident match',
  uncertain: 'uncertain match — check it',
  missing: 'not mapped',
};

function facingLabel(q: QuaternionTuple): string {
  return FACINGS.find((f) => quatEquals(f.q, q, 1e-4))?.value ?? 'custom';
}

/**
 * The select's leading-edge stripe. A validation error outranks the confidence: a row the
 * validation rejected is the one to act on, whatever the auto-mapper thought of it.
 */
function stripeClass(confidence: MappingConfidence, hasError: boolean): string {
  if (hasError) return 'bad';
  if (confidence === 'confident') return 'ok';
  return confidence === 'uncertain' ? 'warn' : 'none';
}

export function mountMappingPanel(
  container: HTMLElement,
  ctx: EditorContext,
  controllers: Controllers,
): PanelHandle {
  const { store } = ctx;
  /** The last correction we did NOT set ourselves — i.e. whatever auto-mapping guessed. */
  let autoCorrection: QuaternionTuple = store.getState().mapping.worldCorrection;
  let settingCorrection = false;
  let presetId = '';

  function render(root: HTMLElement): void {
    const state = store.getState();
    const mapping = state.mapping;
    const errorsByBone = new Map<CanonicalBoneId, string>();
    for (const issue of mapping.validation?.errors ?? []) {
      if (issue.bone && !errorsByBone.has(issue.bone)) errorsByBone.set(issue.bone, issue.message);
    }

    const sourceOptions: Option[] = [
      { value: '', label: '— none —' },
      ...mapping.sourceBoneNames.map((name) => ({ value: name, label: name })),
    ];

    /*
     * Two columns, not four: the old `<table>` (canonical / source / confidence badge / warning)
     * needed ~520px and was cut off in a 340px column (audit B). The confidence became the
     * select's leading edge stripe, and the warning is an icon that only exists when it applies.
     */
    const list = el('div', { class: 'maplist' });
    for (const bone of CANONICAL_BONE_IDS) {
      const current = mapping.draft[bone] ?? '';
      const confidence = mapping.confidence[bone] ?? (current ? 'confident' : 'missing');
      const issue = errorsByBone.get(bone);
      const source = el(
        'span',
        {
          class: `src ${stripeClass(confidence, issue !== undefined)}`,
          title: CONFIDENCE_TITLE[confidence],
        },
        [
          select(
            // Keep an unknown persisted name selectable instead of silently dropping it.
            current && !mapping.sourceBoneNames.includes(current)
              ? [...sourceOptions, { value: current, label: `${current} (missing)` }]
              : sourceOptions,
            current,
            (value) => store.actions.setMappingEntry(bone, value === '' ? null : value),
            { 'data-role': 'map-select' },
          ),
        ],
      );
      list.appendChild(
        el('div', { class: 'maprow', 'data-bone': bone }, [
          el('span', { class: 'cn', text: bone === 'Root' ? 'Root (optional)' : bone }),
          source,
          // The `title` sits on a wrapper, not on the `<svg>`: a `title` *attribute* on an SVG
          // element renders no tooltip (SVG wants a `<title>` child, which would leak into
          // `textContent` and break the "an icon adds no label text" rule).
          issue ? el('span', { class: 'warn', title: issue }, [icon('alert', 12)]) : null,
        ]),
      );
    }

    /*
     * Section ORDER is this panel's contract: the mapping panel test addresses "Rest-space
     * correction" as `.sec:nth-of-type(3)`, so the number and order of `<section>` children is
     * load-bearing (Mapping · Validation · Rest-space correction · Presets).
     */
    root.appendChild(
      section(
        'Mapping',
        [
          el('div', { class: 'buttons' }, [
            button([icon('zap'), 'Auto-map'], () => controllers.mapping.autoMap()),
            button([icon('check'), 'Validate'], () => controllers.mapping.validate()),
          ]),
          list,
        ],
        { icon: 'map', meta: `${mapping.sourceBoneNames.length} source bones` },
      ),
    );

    const validation = mapping.validation;
    const summary: HTMLElement[] = [];
    if (!validation) {
      summary.push(note('Not validated yet.'));
    } else if (validation.ok && validation.errors.length === 0) {
      summary.push(note('Mapping is valid.', 'note ok'));
    }
    if (validation && validation.errors.length > 0) {
      const errors = el('ul', { class: 'errlist' });
      for (const issue of validation.errors) {
        errors.appendChild(
          el('li', { text: `${issue.bone ? `${issue.bone}: ` : ''}${issue.message}` }),
        );
      }
      summary.push(errors);
    }
    // Warnings never block Confirm; they are the "looks odd, have a look" half of validateMapping.
    if (validation && validation.warnings.length > 0) {
      summary.push(note(`Warnings (${validation.warnings.length}):`));
      const warnings = el('ul', { class: 'warnlist' });
      for (const issue of validation.warnings) {
        warnings.appendChild(
          el('li', { text: `${issue.bone ? `${issue.bone}: ` : ''}${issue.message}` }),
        );
      }
      summary.push(warnings);
    }
    root.appendChild(section('Validation', summary, { icon: 'check' }));

    root.appendChild(
      section('Rest-space correction', [
        field(
          'Facing',
          select(
            facingLabel(mapping.worldCorrection) === 'custom'
              ? [...FACINGS.map((f) => ({ value: f.value, label: f.label })), { value: 'custom', label: 'custom (from auto-guess)' }]
              : FACINGS.map((f) => ({ value: f.value, label: f.label })),
            facingLabel(mapping.worldCorrection),
            (value) => {
              const facing = FACINGS.find((f) => f.value === value);
              if (!facing) return;
              settingCorrection = true;
              store.actions.setWorldCorrection(facing.q);
              settingCorrection = false;
            },
            { 'data-role': 'facing' },
          ),
        ),
        note(`auto: ${facingLabel(autoCorrection)}`),
      ], { icon: 'target' }),
    );

    const presets = mapping.presets;
    if (presetId && !presets.some((p) => p.id === presetId)) presetId = '';
    // Collapsed on first look: presets are a power feature, and the three blocks above them are
    // the ones a first mapping needs (the user's own toggle wins from then on).
    root.appendChild(
      section('Presets', [
        select(
          [
            { value: '', label: '— select preset —' },
            ...presets.map((preset) => ({ value: preset.id, label: preset.name })),
          ],
          presetId,
          (value) => {
            presetId = value;
          },
          { 'data-role': 'preset' },
        ),
        el('div', { class: 'buttons' }, [
          button(
            [icon('upload'), 'Load'],
            () => {
              if (presetId) runAsync(controllers.mapping.loadPreset(presetId));
            },
            { disabled: presets.length === 0 },
          ),
          button([icon('save'), 'Save as…'], () => {
            runAsync(
              dialogs.text({
                title: 'Save mapping preset',
                label: 'Preset name',
                value: state.project?.name ?? 'preset',
                confirmLabel: 'Save',
              }),
              (name) => {
                if (name) runAsync(controllers.mapping.savePreset(name));
              },
            );
          }),
          button(
            [icon('trash'), 'Delete'],
            () => {
              if (!presetId) return;
              const preset = presets.find((p) => p.id === presetId);
              if (!preset) return;
              runAsync(
                dialogs.confirm({
                  title: 'Delete preset',
                  message: `Delete preset "${preset.name}"?`,
                  confirmLabel: 'Delete',
                  danger: true,
                }),
                (confirmed) => {
                  if (confirmed) runAsync(controllers.mapping.deletePreset(preset.id));
                },
              );
            },
            { disabled: presets.length === 0 },
          ),
        ]),
      ], { icon: 'folder', defaultCollapsed: true }),
    );
  }

  const instance = createPanel(container, ctx, render, {
    className: 'mapping',
    slices: [(s) => s.mapping, (s) => s.project],
  });

  // The preset list lives in the store but nobody else fills it; MAP mode is where it is used.
  runAsync(controllers.mapping.refreshPresets());

  // Track the auto-guess separately so overriding the facing does not erase what was guessed.
  instance.own(
    store.select(
      (s) => s.mapping.worldCorrection,
      (value) => {
        if (!settingCorrection) autoCorrection = value;
      },
    ),
  );

  return instance;
}
